import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetModelApplicationReportLazyQuery } from '../generated/graphql';

/** A single model record collected across all pages. */
interface ModelRecord {
    id: string;
    applicationName: string | null;
    projectId: string | null;
    updatedTime: string | null;
}

/** Aggregated row for a single application name. */
export interface ApplicationUsageRow {
    applicationName: string;
    modelCount: number;
    projectCount: number;
    projectIds: string[];
}

/** The full report returned by the hook. */
export interface ApplicationUsageReport {
    /** All rows grouped by applicationName, sorted by modelCount descending. */
    rows: ApplicationUsageRow[];
    /** Total models that matched the date range. */
    totalModels: number;
    /** Total distinct projects that matched the date range. */
    totalProjects: number;
    /** Total model records fetched (before date filtering). */
    totalFetched: number;
}

export interface UseApplicationUsageReportOptions {
    /** Number of months to look back from today. Defaults to 12. */
    monthsBack?: number;
}

export interface UseApplicationUsageReportResult {
    /** Kick off the paginated fetch. Safe to call multiple times (resets state). */
    fetch: () => void;
    /** The computed report, or null while still loading. */
    report: ApplicationUsageReport | null;
    /** True while pages are still being fetched. */
    loading: boolean;
    /** Error message if something went wrong. */
    error: string | null;
    /** Progress indicator: how many model records fetched so far. */
    fetchedCount: number;
}

/**
 * Hook that auto-paginates through **all** models via the
 * `getModelApplicationReport` query and aggregates application usage
 * statistics for the last N months.
 *
 * Usage:
 * ```tsx
 * const { fetch, report, loading, error, fetchedCount } = useApplicationUsageReport({ monthsBack: 12 });
 *
 * useEffect(() => { fetch(); }, [fetch]);
 *
 * if (report) {
 *   report.rows.forEach(r =>
 *     console.log(r.applicationName, r.modelCount, r.projectCount)
 *   );
 * }
 * ```
 */
export function useApplicationUsageReport(
    options?: UseApplicationUsageReportOptions
): UseApplicationUsageReportResult {
    const monthsBack = options?.monthsBack ?? 12;

    const [allModels, setAllModels] = useState<ModelRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);
    const abortRef = useRef(false);

    const [executeQuery] = useGetModelApplicationReportLazyQuery({ fetchPolicy: 'network-only' });

    /** Epoch-seconds cutoff: N months ago from today. */
    const cutoffEpoch = useMemo(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - monthsBack);
        return Math.floor(d.getTime() / 1000);
    }, [monthsBack]);

    /** Recursively fetch all pages, collecting results into `accumulated`. */
    const fetchAllPages: (cursor: string | null, accumulated: ModelRecord[]) => Promise<ModelRecord[]> = useCallback(
        async (cursor: string | null, accumulated: ModelRecord[]) => {
            if (abortRef.current) return accumulated;

            const { data, error: queryError } = await executeQuery({
                variables: { after: cursor },
            });

            if (queryError) {
                throw new Error(queryError.message);
            }

            const nodes = data?.ModelList?.nodes ?? [];
            const mapped: ModelRecord[] = nodes
                .filter((n): n is NonNullable<typeof n> => n != null)
                .map((n) => ({
                    id: n.id ?? '',
                    applicationName: n.applicationName ?? null,
                    projectId: n.projectId ?? null,
                    updatedTime: n.updatedTime ?? null,
                }));

            const updated = [...accumulated, ...mapped];

            // Update progress
            setAllModels(updated);

            const pageInfo = data?.ModelList?.pageInfo;
            if (pageInfo?.hasNextPage && pageInfo.endCursor) {
                return fetchAllPages(pageInfo.endCursor, updated);
            }

            return updated;
        },
        [executeQuery]
    );

    const fetch = useCallback(() => {
        abortRef.current = false;
        setAllModels([]);
        setError(null);
        setDone(false);
        setLoading(true);

        fetchAllPages(null, [])
            .then((final: ModelRecord[]) => {
                if (!abortRef.current) {
                    setAllModels(final);
                    setDone(true);
                }
            })
            .catch((err) => {
                if (!abortRef.current) {
                    setError(err?.message ?? 'Unknown error fetching model data');
                }
            })
            .finally(() => {
                if (!abortRef.current) {
                    setLoading(false);
                }
            });
    }, [fetchAllPages]);

    // Abort on unmount
    useEffect(() => {
        return () => {
            abortRef.current = true;
        };
    }, []);

    /** Compute the report once all pages have been collected. */
    const report = useMemo<ApplicationUsageReport | null>(() => {
        if (!done) return null;

        // 1. Filter to models updated within the date range
        const filtered = allModels.filter((m) => {
            if (!m.updatedTime) return false;
            const epoch = Number(m.updatedTime);
            return !isNaN(epoch) && epoch >= cutoffEpoch;
        });

        // 2. Group by applicationName
        const byApp = new Map<string, { modelIds: Set<string>; projectIds: Set<string> }>();

        for (const m of filtered) {
            const appName = m.applicationName?.trim() || '(Unknown)';
            let entry = byApp.get(appName);
            if (!entry) {
                entry = { modelIds: new Set(), projectIds: new Set() };
                byApp.set(appName, entry);
            }
            entry.modelIds.add(m.id);
            if (m.projectId) {
                entry.projectIds.add(m.projectId);
            }
        }

        // 3. Build rows sorted by model count descending
        const rows: ApplicationUsageRow[] = Array.from(byApp.entries())
            .map(([appName, entry]) => ({
                applicationName: appName,
                modelCount: entry.modelIds.size,
                projectCount: entry.projectIds.size,
                projectIds: Array.from(entry.projectIds),
            }))
            .sort((a, b) => b.modelCount - a.modelCount);

        // 4. Totals
        const allProjectIds = new Set<string>();
        byApp.forEach((entry) => {
            entry.projectIds.forEach((id: string) => allProjectIds.add(id));
        });

        return {
            rows,
            totalModels: filtered.length,
            totalProjects: allProjectIds.size,
            totalFetched: allModels.length,
        };
    }, [done, allModels, cutoffEpoch]);

    return {
        fetch,
        report,
        loading,
        error,
        fetchedCount: allModels.length,
    };
}
